/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { StudioRole, StudioPermissions } from '../types/studio';

// Role hierarchy levels (higher number = more permissions)
const ROLE_HIERARCHY: Record<StudioRole, number> = {
  [StudioRole.OWNER]: 7,
  [StudioRole.MANAGER]: 6,
  [StudioRole.ADMIN]: 5,
  [StudioRole.INSTRUCTOR]: 4,
  [StudioRole.PARENT]: 3,
  [StudioRole.STUDENT]: 2,
  [StudioRole.MEMBER]: 1,
};

/**
 * Check if a role has sufficient level for a required role
 */
export const hasRoleLevel = (
  userRole: StudioRole | null | undefined,
  requiredRole: StudioRole
): boolean => {
  if (!userRole) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

/**
 * Calculate permissions based on user role
 */
export const calculatePermissions = (
  role: StudioRole | null | undefined
): StudioPermissions => {
  if (!role) {
    return {
      canViewStudio: false,
      canEditStudio: false,
      canDeleteStudio: false,
      canInviteMembers: false,
      canViewMembers: false,
      canManageMembers: false,
      canViewBilling: false,
      canManageBilling: false,
      canAccessTools: false,
      canConfigureTools: false,
    };
  }

  const permissions: StudioPermissions = {
    canViewStudio: true, // All roles can view studio
    canEditStudio: hasRoleLevel(role, StudioRole.MANAGER),
    canDeleteStudio: role === StudioRole.OWNER,
    // Only Owner and Manager can invite and manage members
    canInviteMembers: role === StudioRole.OWNER || role === StudioRole.MANAGER,
    canViewMembers: true, // All roles can view members list
    canManageMembers: role === StudioRole.OWNER || role === StudioRole.MANAGER,
    canViewBilling: hasRoleLevel(role, StudioRole.MANAGER),
    canManageBilling: role === StudioRole.OWNER,
    canAccessTools: true, // All roles can access tools
    canConfigureTools: hasRoleLevel(role, StudioRole.MANAGER),
  };

  return permissions;
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (
  permissions: StudioPermissions | null | undefined,
  permission: keyof StudioPermissions
): boolean => {
  if (!permissions) return false;
  return permissions[permission];
};

/**
 * Check if user can perform an action on another user based on role hierarchy
 */
export const canManageUser = (
  managerRole: StudioRole | null | undefined,
  targetRole: StudioRole
): boolean => {
  if (!managerRole) return false;

  // Only Owner and Manager can manage other users
  if (managerRole !== StudioRole.OWNER && managerRole !== StudioRole.MANAGER) {
    return false;
  }

  // Owners can manage everyone except other owners
  if (managerRole === StudioRole.OWNER) {
    return targetRole !== StudioRole.OWNER;
  }

  // Managers can manage all roles below them (admin, instructor, parent, student, member)
  if (managerRole === StudioRole.MANAGER) {
    return targetRole !== StudioRole.OWNER && targetRole !== StudioRole.MANAGER;
  }

  return false;
};

/**
 * Get available roles that a user can assign to others (matches the
 * backend): owners can assign any role, including owner; everyone else
 * only roles strictly below their own level.
 */
export const getAssignableRoles = (
  userRole: StudioRole | null | undefined
): StudioRole[] => {
  if (!userRole) return [];

  const userLevel = ROLE_HIERARCHY[userRole];
  const isOwner = userRole === StudioRole.OWNER;

  return Object.entries(ROLE_HIERARCHY)
    .filter(([, level]) => isOwner || level < userLevel)
    .map(([role]) => role as StudioRole)
    .sort((a, b) => ROLE_HIERARCHY[b] - ROLE_HIERARCHY[a]); // Sort by hierarchy (highest first)
};

/**
 * Check if a role change is valid
 */
export const isValidRoleChange = (
  managerRole: StudioRole | null | undefined,
  currentRole: StudioRole,
  newRole: StudioRole
): boolean => {
  if (!managerRole) return false;

  // Can't change your own role
  if (managerRole === currentRole) return false;

  // Must be able to manage the current user
  if (!canManageUser(managerRole, currentRole)) return false;

  // Must be able to assign the new role
  const assignableRoles = getAssignableRoles(managerRole);
  return assignableRoles.includes(newRole);
};

/**
 * Get user-friendly permission descriptions
 */
export const getPermissionDescription = (
  permission: keyof StudioPermissions
): string => {
  const descriptions: Record<keyof StudioPermissions, string> = {
    canViewStudio: 'View studio information and content',
    canEditStudio: 'Edit studio settings and information',
    canDeleteStudio: 'Delete the studio permanently',
    canInviteMembers: 'Invite new members to the studio',
    canViewMembers: 'View studio members list and profiles',
    canManageMembers: 'Manage member roles and remove members',
    canViewBilling: 'View billing information and subscription details',
    canManageBilling: 'Manage billing, subscriptions, and payment methods',
    canAccessTools: 'Access studio tools and features',
    canConfigureTools: 'Configure and customize studio tools',
  };

  return descriptions[permission];
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: StudioRole): string => {
  const displayNames: Record<StudioRole, string> = {
    [StudioRole.OWNER]: 'Owner',
    [StudioRole.MANAGER]: 'Manager',
    [StudioRole.ADMIN]: 'Admin',
    [StudioRole.INSTRUCTOR]: 'Instructor',
    [StudioRole.PARENT]: 'Parent',
    [StudioRole.STUDENT]: 'Student',
    [StudioRole.MEMBER]: 'Member',
  };

  return displayNames[role];
};

/**
 * Get role color for UI display
 */
export const getRoleColor = (role: StudioRole): string => {
  const colors: Record<StudioRole, string> = {
    [StudioRole.OWNER]: '#1976d2', // Primary brand color
    [StudioRole.MANAGER]: '#0d47a1', // Dark blue
    [StudioRole.ADMIN]: '#388e3c', // Green
    [StudioRole.INSTRUCTOR]: '#ff9800', // Orange
    [StudioRole.PARENT]: '#9c27b0', // Purple
    [StudioRole.STUDENT]: '#00bcd4', // Cyan
    [StudioRole.MEMBER]: '#616161', // Gray
  };

  return colors[role];
};
